import { CSSProperties } from "react";
import { Card, HexColor, Shop } from "@/types/v2";
import { getCardSection } from "@/lib/cards";
import { useFeatures } from "@/hooks/useFeatures";
import { useCardAnimations } from "@/hooks/useCardAnimations";
import { useCardActions } from "@/hooks/useCardActions";
import { cn, gradientFromColor, getTextColor } from "@/lib/utils";
import { DEFAULT_CARD_COLOR } from "@/lib/const";
import { ColorPicker } from "@/components/Dashboard/components/ColorPicker/ColorPicker";

export interface RoCardPreviewProps {
  cardData: Card;
  shop: Shop;
}

export const RoCardPreview = ({ cardData, shop }: RoCardPreviewProps) => {
  const { features } = useFeatures(shop);
  const { handleColorChange } = useCardActions(cardData.id);
  const { getBackgroundColor, animationName, automationAction } =
    useCardAnimations({
      cardData,
      isCardColorSyncEnabled: features.isCardColorSyncEnabled,
      isCardColorEnabled: features.isCardColorEnabled,
      isPromiseTimeEnabled: features.isPromiseTimeEnabled,
      isDragging: false,
      startTime: null,
      promiseTimePastDueColor: shop.shopConfig?.promiseTime?.pastDueColor,
    });
  const backgroundColor = getBackgroundColor();

  return (
    <div
      className={cn(
        "relative rounded-md",
        automationAction("wiggle") && "animate-wiggle"
      )}
      style={
        {
          backgroundColor,
          backgroundImage: gradientFromColor(backgroundColor),
        } as CSSProperties
      }
    >
      <div
        className={cn(
          "flex flex-col gap-1 rounded-md p-3",
          automationAction("flash") && `animate-${animationName}`
        )}
        style={
          {
            color: getTextColor(backgroundColor),
            "--flash-base-color": backgroundColor,
          } as CSSProperties
        }
      >
        <p className="text-lg font-bold leading-tight">
          {getCardSection(cardData, shop, "primaryField")}
        </p>
        <p className="text-base font-semibold leading-tight">
          {getCardSection(cardData, shop, "secondaryField")}
        </p>
        <p className="text-sm leading-tight opacity-80">
          {getCardSection(cardData, shop, "tertiaryField")}
        </p>
        <div className="absolute bottom-2 right-2">
          <ColorPicker
            color={
              cardData.color
                ? cardData.color.includes("#")
                  ? (cardData.color as HexColor)
                  : `#${cardData.color}`
                : DEFAULT_CARD_COLOR
            }
            onColorChange={handleColorChange}
            triggerSize="sm"
          />
        </div>
      </div>
    </div>
  );
};
